import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  createAuth47Challenge,
  createLnurlAuthChallenge,
  createLnurlLinkChallenge,
  pollAuth47Status,
  pollLnurlAuthStatus,
  pollLnurlLinkStatus,
} from '../api'
import type {
  AuthResponse,
  PublicKeyAuthAction,
  PublicKeyAuthChallenge,
  PublicKeyAuthMethod,
} from '../types'

const INITIAL_POLL_DELAY_MS = 1000
const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 120_000

interface UsePublicKeyAuthFlowOptions {
  method: PublicKeyAuthMethod
  action: PublicKeyAuthAction
  enabled: boolean
  referralCode?: string | null
  onAuthComplete: (auth: AuthResponse) => Promise<void>
  onLinkComplete: () => Promise<void>
}

interface UsePublicKeyAuthFlowReturn {
  challenge: PublicKeyAuthChallenge | null
  copyValue: string
  error: Error | null
  isLoading: boolean
  isPolling: boolean
  reset: () => void
}

function isAuthComplete(auth: AuthResponse | null | undefined): auth is AuthResponse {
  return !!auth?.refreshToken
}

function expiredMessage(action: PublicKeyAuthAction): string {
  if (action === 'link') {
    return 'This link code expired. Generate a new code and try again.'
  }

  if (action === 'signup') {
    return 'This sign-up code expired. Generate a new code and try again.'
  }

  return 'This sign-in code expired. Generate a new code and try again.'
}

export function usePublicKeyAuthFlow({
  method,
  action,
  enabled,
  referralCode,
  onAuthComplete,
  onLinkComplete,
}: UsePublicKeyAuthFlowOptions): UsePublicKeyAuthFlowReturn {
  const [challenge, setChallenge] = useState<PublicKeyAuthChallenge | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [resetCounter, setResetCounter] = useState(0)
  const completedRef = useRef(false)
  const onAuthCompleteRef = useRef(onAuthComplete)
  const onLinkCompleteRef = useRef(onLinkComplete)

  useEffect(() => {
    onAuthCompleteRef.current = onAuthComplete
    onLinkCompleteRef.current = onLinkComplete
  }, [onAuthComplete, onLinkComplete])

  useEffect(() => {
    if (!enabled) {
      setChallenge(null)
      setError(null)
      setIsLoading(false)
      setIsPolling(false)
      completedRef.current = false
      return
    }

    let cancelled = false
    completedRef.current = false
    setChallenge(null)
    setError(null)
    setIsLoading(true)
    setIsPolling(false)

    async function loadChallenge() {
      try {
        if (method === 'lnurl') {
          const response =
            action === 'link'
              ? await createLnurlLinkChallenge()
              : await createLnurlAuthChallenge(action, {
                  referralCode: action === 'signup' ? referralCode : null,
                })
          if (!cancelled) {
            setChallenge({
              method: 'lnurl',
              k1: response.k1,
              payload: response.lnurl,
              uuid: response.uuid,
            })
          }
          return
        }

        const response = await createAuth47Challenge({
          referralCode: action === 'signup' ? referralCode : null,
          skipAuthRefresh: action !== 'link',
        })
        if (!cancelled) {
          setChallenge({ method: 'auth47', payload: response.challenge, uuid: response.uuid })
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadChallenge()

    return () => {
      cancelled = true
    }
  }, [action, enabled, method, referralCode, resetCounter])

  useEffect(() => {
    if (!enabled || !challenge || completedRef.current) return

    const activeChallenge = challenge
    let cancelled = false
    const startedAt = Date.now()
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    async function poll() {
      if (cancelled || completedRef.current) return
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setIsPolling(false)
        setError(new Error(expiredMessage(action)))
        return
      }

      setIsPolling(true)

      try {
        if (activeChallenge.method === 'lnurl') {
          const request = { k1: activeChallenge.k1, uuid: activeChallenge.uuid }
          if (action === 'link') {
            const status = await pollLnurlLinkStatus(request)
            if (status.linked) {
              completedRef.current = true
              await onLinkCompleteRef.current()
              return
            }
          } else {
            const status = await pollLnurlAuthStatus(request)
            if (status.status === 'complete' && isAuthComplete(status.auth)) {
              completedRef.current = true
              await onAuthCompleteRef.current(status.auth)
              return
            }
          }
        } else {
          const status = await pollAuth47Status(activeChallenge.uuid)
          if (action === 'link' && status.status === 'complete') {
            completedRef.current = true
            await onLinkCompleteRef.current()
            return
          }

          if (status.status === 'complete' && isAuthComplete(status.auth)) {
            completedRef.current = true
            await onAuthCompleteRef.current(status.auth)
            return
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      }

      if (!cancelled && !completedRef.current) {
        timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    timeoutId = setTimeout(poll, INITIAL_POLL_DELAY_MS)

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      setIsPolling(false)
    }
  }, [action, challenge, enabled])

  const reset = useCallback(() => {
    completedRef.current = false
    setResetCounter((value) => value + 1)
  }, [])

  const copyValue = useMemo(() => {
    if (!challenge) return ''
    return challenge.payload
  }, [challenge])

  return {
    challenge,
    copyValue,
    error,
    isLoading,
    isPolling,
    reset,
  }
}
